// Internal portal — Transactions tab. Searchable purchase-receipt
// table with an inline expandable detail row that includes the
// receipt image (loaded via the R2 proxy token).

import { useMemo, useState } from "react"
import {
  ChevronDown,
  ChevronUp,
  ImageIcon,
  Receipt,
  Search,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { env } from "@/config/env"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type {
  OTTraceability,
  TraceabilityTransactionEntry,
} from "@/features/transit/api/types"

interface Props {
  traceability: OTTraceability
}

export function TraceabilityTransactionsTab({ traceability }: Props) {
  const { t } = useTranslation("transit")
  const [search, setSearch] = useState("")
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const transactions = traceability.transactions

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return transactions
    return transactions.filter((tx) => {
      return (
        tx.receiptNumber.toLowerCase().includes(term) ||
        tx.producerName.toLowerCase().includes(term) ||
        (tx.producerRef ?? "").toLowerCase().includes(term)
      )
    })
  }, [transactions, search])

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Receipt className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          {t("trace.transactions.title", { count: transactions.length })}
        </h2>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("trace.transactions.searchPlaceholder")}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          {search
            ? t("trace.transactions.searchEmpty")
            : t("trace.transactions.empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.receipt")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.date")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.producer")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.bags")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.kg")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.amount")}
                </th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((tx) => {
                const isOpen = expandedId === tx.id
                return (
                  <Row
                    key={tx.id}
                    tx={tx}
                    isOpen={isOpen}
                    onToggle={() => setExpandedId(isOpen ? null : tx.id)}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function Row({
  tx,
  isOpen,
  onToggle,
}: {
  tx: TraceabilityTransactionEntry
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation("transit")
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-muted/40"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <td className="px-3 py-2 font-medium text-foreground">
          {tx.receiptNumber || `#${tx.id}`}
        </td>
        <td className="px-3 py-2 text-muted-foreground">{tx.date ?? "—"}</td>
        <td className="px-3 py-2">
          <div className="font-medium text-foreground">{tx.producerName}</div>
          {tx.producerRef ? (
            <div className="text-[10px] text-muted-foreground">
              {tx.producerRef}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{tx.numberOfBags}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatNumber(tx.netKilograms)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatNumber(tx.totalPrice)} CFA
        </td>
        <td className="px-3 py-2 text-right text-muted-foreground">
          {isOpen ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </td>
      </tr>
      {isOpen ? (
        <tr className="bg-muted/30">
          <td colSpan={7} className="px-3 py-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Fact
                  label={t("trace.transactions.detail.basePrice")}
                  value={`${formatNumber(tx.basePrice, 2)} CFA / kg`}
                />
                <Fact
                  label={t("trace.transactions.detail.netKilograms")}
                  value={`${formatNumber(tx.netKilograms)} kg`}
                />
                <Fact
                  label={t("trace.transactions.detail.bags")}
                  value={tx.numberOfBags.toString()}
                />
                <Fact
                  label={t("trace.transactions.detail.totalPrice")}
                  value={`${formatNumber(tx.totalPrice)} CFA`}
                />
                <Fact
                  label={t("trace.transactions.detail.deliveryId")}
                  value={tx.deliveryId ? `#${tx.deliveryId}` : "—"}
                />
                <Fact
                  label={t("trace.transactions.detail.certifications")}
                  value={tx.certificates.join(", ") || "—"}
                />
              </div>
              <ReceiptImage tx={tx} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded-md border bg-card px-3 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  )
}

function ReceiptImage({ tx }: { tx: TraceabilityTransactionEntry }) {
  const { t } = useTranslation("transit")
  const url = useMemo(() => proxiedImageUrl(tx.imageUrl), [tx.imageUrl])

  if (!tx.hasImage) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border bg-card p-6 text-xs text-muted-foreground">
        <ImageIcon className="mr-2 size-4" />
        {t("trace.transactions.detail.noImage")}
      </div>
    )
  }
  if (!url) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-md border bg-card p-6 text-xs text-muted-foreground">
        <ImageIcon className="size-4" />
        <span>{t("trace.transactions.detail.imageUnavailable")}</span>
      </div>
    )
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex max-h-72 overflow-hidden rounded-md border bg-card"
    >
      <img
        src={url}
        alt={tx.receiptNumber || "Receipt"}
        loading="lazy"
        className="h-full w-full object-contain"
      />
    </a>
  )
}

// proxiedImageUrl appends the R2 proxy token to the receipt URL. The
// upstream R2 worker requires the token; without it the image would
// 401. Returns null when the token is unset (FE renders a placeholder).
function proxiedImageUrl(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!env.r2ProxyToken) return null
  try {
    const url = new URL(raw)
    if (!url.searchParams.has("token")) {
      url.searchParams.set("token", env.r2ProxyToken)
    }
    return url.toString()
  } catch {
    return null
  }
}

function formatNumber(value: number, fractionDigits = 0): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}
